import { Type } from '@angular/core';
import { Route, Router } from '@angular/router';
import { NavigationItem } from '../../core/context/current-context.model';
import { REGISTERED_APP_ROUTES, RouteRegistryEntry } from '../../core/navigation/navigation-route-registry';
import { SUPPLEMENTAL_NAVIGATION_ITEMS } from '../../core/navigation/navigation-supplemental-items';
import { WorkflowModuleSurfaceRegistry } from './workflow-module-surface.registry';

const ROUTE_SURFACE_SKIP_PREFIXES = ['/login', '/dashboard', '/workflow', '/dynamic'];

const MODULE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  growth: ['growth-platform']
};

type SurfaceLoader = () => Promise<Type<unknown>>;

export function registerRouteBasedWorkflowSurfaces(registry: WorkflowModuleSurfaceRegistry, router: Router): void {
  const routeMetadata = buildRouteMetadataIndex(SUPPLEMENTAL_NAVIGATION_ITEMS);
  const routeKeys = [...routeMetadata.keys()].sort((a, b) => b.length - a.length);
  const routerLoaders = buildRouterSurfaceLoaders(router.config);
  const registeredKeys = new Set<string>();

  for (const routeEntry of REGISTERED_APP_ROUTES) {
    const normalizedRoute = normalizeRoute(routeEntry.route);
    if (!normalizedRoute || shouldSkipRoute(normalizedRoute, routeEntry)) {
      continue;
    }

    const metadata = resolveRouteMetadata(normalizedRoute, routeMetadata, routeKeys);
    if (!metadata) {
      continue;
    }

    const moduleKeys = [metadata.moduleKey, ...(MODULE_ALIASES[metadata.moduleKey] ?? [])];
    for (const moduleKey of moduleKeys) {
      const registrationKey = `${moduleKey.toLowerCase()}::${normalizedRoute.toLowerCase()}`;
      if (registeredKeys.has(registrationKey)) {
        continue;
      }

      registry.register({
        moduleKey,
        surfaceKey: normalizedRoute,
        titleKey: metadata.titleKey,
        descriptionKey: metadata.descriptionKey,
        fullPageRouteTemplate: normalizedRoute,
        loadComponent: resolveSurfaceLoader(normalizedRoute, routerLoaders)
      });

      registeredKeys.add(registrationKey);
    }
  }
}

interface RouteSurfaceMetadata {
  moduleKey: string;
  titleKey: string;
  descriptionKey?: string;
}

function buildRouteMetadataIndex(items: readonly NavigationItem[]): Map<string, RouteSurfaceMetadata> {
  const map = new Map<string, RouteSurfaceMetadata>();
  for (const item of items) {
    const route = normalizeRoute(item.route);
    if (!route) {
      continue;
    }

    const moduleKey = (item.moduleKeyCanonical || item.moduleKey || '').trim().toLowerCase();
    if (!moduleKey) {
      continue;
    }

    map.set(route.toLowerCase(), {
      moduleKey,
      titleKey: item.label || route,
      descriptionKey: item.capabilityKey || undefined
    });
  }

  return map;
}

function resolveRouteMetadata(
  route: string,
  routeMetadata: ReadonlyMap<string, RouteSurfaceMetadata>,
  routeKeys: readonly string[]
): RouteSurfaceMetadata | null {
  const direct = routeMetadata.get(route.toLowerCase());
  if (direct) {
    return direct;
  }

  const lower = route.toLowerCase();
  for (const key of routeKeys) {
    if (lower.startsWith(`${key}/`)) {
      const match = routeMetadata.get(key);
      if (match) {
        return {
          moduleKey: match.moduleKey,
          titleKey: route
        };
      }
    }
  }

  return null;
}

function shouldSkipRoute(route: string, routeEntry: RouteRegistryEntry): boolean {
  if (routeEntry.excludeFromMenuCheck) {
    return true;
  }

  return ROUTE_SURFACE_SKIP_PREFIXES.some(prefix => route === prefix || route.startsWith(`${prefix}/`));
}

function normalizeRoute(route: string): string {
  const normalized = `${route || ''}`.trim();
  if (!normalized.startsWith('/')) {
    return normalized ? `/${normalized}` : '';
  }

  return normalized;
}

function resolveSurfaceLoader(route: string, routerLoaders: ReadonlyMap<string, SurfaceLoader>): SurfaceLoader {
  const routerLoader = routerLoaders.get(route.toLowerCase());
  if (routerLoader) {
    return routerLoader;
  }

  return () =>
    import('./workflow-route-task-surface.component').then(module => module.WorkflowRouteTaskSurfaceComponent);
}

function buildRouterSurfaceLoaders(routes: readonly Route[]): ReadonlyMap<string, SurfaceLoader> {
  const map = new Map<string, SurfaceLoader>();
  collectRouteSurfaceLoaders(routes, '', map);
  return map;
}

function collectRouteSurfaceLoaders(
  routes: readonly Route[],
  parentPath: string,
  target: Map<string, SurfaceLoader>
): void {
  for (const route of routes) {
    const currentPath = joinRoutePath(parentPath, route.path);
    const currentPathKey = currentPath.toLowerCase();
    if (route.component && currentPath && !target.has(currentPathKey)) {
      const componentType = route.component as Type<unknown>;
      target.set(currentPathKey, () => Promise.resolve(componentType));
    } else if (route.loadComponent && currentPath && !target.has(currentPathKey)) {
      target.set(currentPathKey, async () => {
        const loaded = await route.loadComponent!();
        return loaded as Type<unknown>;
      });
    }

    if (route.children?.length) {
      collectRouteSurfaceLoaders(route.children, currentPath, target);
    }
  }
}

function joinRoutePath(parentPath: string, childPath: string | undefined): string {
  const normalizedParent = parentPath.trim();
  const normalizedChild = `${childPath ?? ''}`.trim();
  if (!normalizedParent && !normalizedChild) {
    return '';
  }

  if (!normalizedParent) {
    return normalizeRoute(normalizedChild);
  }

  if (!normalizedChild) {
    return normalizeRoute(normalizedParent);
  }

  return normalizeRoute(`${normalizedParent}/${normalizedChild}`);
}
